import "mapbox-gl/dist/mapbox-gl.css";
import React from "react";
import classes from "./AllDriversMap.module.css";
import ModalWrapper from "../../modalWrapper/ModalWrapper";
import mapMarker from "../../../images/mapMarker.svg";
import Map, { Marker, Popup } from "react-map-gl";
import { getDistance } from "../../../helperFunctions/getDistance";
import { useLocation } from "react-router-dom";
import { SelectElement } from "../../selectElement/SelectElement";
import { LoadingSpinner } from "../../loadingSpinner/LoadingSpinner";
import { useTranslations } from "../../../helperFunctions/customHooks/useTranslations";
import { fetchDriverLocation } from "../../../redux/driver/thunks/getDriverLocation";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import {
  setIsEditModalShown,
  setIsMapShown,
  setIsModalsBgShown,
  setSelectedDriverInfo,
} from "../../../redux/globalFunctions/globalFnSlice";

export function AllDriversMap() {
  const [coordinatesList, setCoordinatesList] = useState([]);
  const [viewState, setViewState] = useState({
    zoom: 7.4,
    latitude: 51.505,
    longitude: -0.09,
  });
  const [expandedGroup, setExpandedGroup] = useState(null);
  const mapRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslations();
  const location = useLocation();
  const { locations } = useSelector((state) => state.driverLocation);
  const [isLoading, setIsLoading] = useState(true);
  const isDriverUrlRoute = location.pathname === "/drivers";
  const text = t.allDriversLocationsMapModal;

  const mapboxToken =
    "pk.eyJ1IjoiYm96aWRhcmszNSIsImEiOiJjbTFxNTdzMWUwOWZwMmpzYjN4eHF5Zmt5In0.fVeom9WzttFClBuhzNSjKg";

  // Fetch driver locations
  useEffect(() => {
    dispatch(fetchDriverLocation({}));
  }, []);

  function getSpiderfyPositions(group) {
    if (!group || group.length === 0) return [];

    if (group.length === 1) {
      return [
        {
          lat: group[0].lat,
          lng: group[0].lng,
          driver: group[0],
        },
      ];
    }

    const angleStep = (2 * Math.PI) / group.length;
    const radius = 0.0002;
    return group.map((driver, i) => {
      const lat = group[0].lat + Math.sin(angleStep * i) * radius;
      const lng = group[0].lng + Math.cos(angleStep * i) * radius;
      return { lat, lng, driver };
    });
  }

  // Geocode addresses to coordinates
  useEffect(() => {
    const fetchCoordinates = async () => {
      if (!locations.length) return;

      try {
        const coordsPromises = locations.map(async (item) => {
          if (!item.dr_current_location) return null;

          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              item.dr_current_location
            )}.json?access_token=${mapboxToken}`
          );
          const data = await response.json();
          if (!data.features || data.features.length === 0) return null;

          const [lng, lat] = data.features[0].center;
          return {
            lat,
            lng,
            driverName: `${item.u_firstname} ${item.u_lastname}`,
            item,
          };
        });

        const coords = await Promise.all(coordsPromises);
        const validCoords = coords.filter(Boolean);

        setCoordinatesList(validCoords);

        if (validCoords.length > 0) {
          setViewState((prev) => ({
            ...prev,
            latitude: validCoords[0].lat + 0.5,
            longitude: validCoords[0].lng,
          }));
        }
      } catch (err) {
        console.error("Error fetching driver coordinates:", err);
      }
    };

    fetchCoordinates();
  }, [locations]);

  // Stop loading spinner
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    if (coordinatesList.length > 0) setIsLoading(false);
    return () => clearTimeout(timer);
  }, [coordinatesList]);

  // Group markers by close coordinates
  const groupedMarkers = [];

  coordinatesList.forEach((coord) => {
    // Always group exact same coordinates together
    const sameLocationGroup = groupedMarkers.find((group) => {
      const first = group[0];
      return (
        first.lat.toFixed(6) === coord.lat.toFixed(6) &&
        first.lng.toFixed(6) === coord.lng.toFixed(6)
      );
    });

    if (sameLocationGroup) {
      sameLocationGroup.push(coord);
      return;
    }

    // Group by proximity only when zoomed out
    if (viewState.zoom <= 14) {
      const nearbyGroup = groupedMarkers.find((group) => {
        const first = group[0];
        const distance = getDistance(
          first.lat,
          first.lng,
          coord.lat,
          coord.lng
        );
        return distance <= 50; // meters
      });

      if (nearbyGroup) {
        nearbyGroup.push(coord);
        return;
      }
    }

    // Otherwise create a new group
    groupedMarkers.push([coord]);
  });

  const handleCloseModal = () => {
    if (
      ["/deliveries", "/dashboard", "/scheduled-deliveries"].includes(
        location.pathname
      )
    ) {
      dispatch(setIsMapShown(false));
      dispatch(setIsModalsBgShown(true));
      dispatch(setIsEditModalShown(true));
    } else {
      dispatch(setIsMapShown(false));
      dispatch(setIsModalsBgShown(false));
    }
  };

  const handleDriverSelect = (driver) => {
    dispatch(setSelectedDriverInfo(driver));
    handleCloseModal();
  };

  function handleFirstClick(index, lat, lng) {
    if (mapRef.current) {
      const map = mapRef.current.getMap();

      map.easeTo({
        center: [lng, lat],
        zoom: 18,
        duration: 500,
        essential: true,
      });
    }
    setExpandedGroup(index);
  }

  return (
    <ModalWrapper
      onClick={handleCloseModal}
      title={text.headerText}
      style={{ width: "600px", height: "500px" }}
    >
      {!isDriverUrlRoute && (
        <SelectElement
          width="200px"
          name="driver"
          id="driver"
          placeholder={text.selectDriver}
          onChange={(e) => {
            const selectedDriver = locations.find(
              (driver) => driver.dr_uid === e.target.value
            );
            if (selectedDriver) handleDriverSelect(selectedDriver);
          }}
          options={locations.map((driver) => ({
            dr_uid: driver.dr_uid,
            fullName: `${driver.u_firstname} ${driver.u_lastname}`,
          }))}
          optionValueKey="dr_uid"
          optionLabelKey="fullName"
        />
      )}

      <div className={classes.mapContainer}>
        {isLoading ? (
          <div className={classes.notificationsDiv}>
            <LoadingSpinner black={true} />
          </div>
        ) : coordinatesList.length > 0 ? (
          <Map
            ref={mapRef}
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            mapStyle="mapbox://styles/mapbox/streets-v11"
            mapboxAccessToken={mapboxToken}
            className={classes.map}
          >
            {groupedMarkers.map((group, groupedIndex) => {
              const baseLat = group[0].lat;
              const baseLng = group[0].lng;

              if (group.length === 1 || expandedGroup !== groupedIndex) {
                return group.map((driver, index) => (
                  <div key={driver.item.dr_uid}>
                    <Marker
                      key={`marker-${driver.item.dr_uid}`}
                      latitude={baseLat}
                      longitude={baseLng}
                      onClick={() => {
                        if (group.length > 1)
                          handleFirstClick(groupedIndex, baseLat, baseLng);
                        else if (!isDriverUrlRoute) {
                          handleDriverSelect(driver.item);
                        } else undefined;
                      }}
                    >
                      <img
                        src={mapMarker}
                        alt=""
                        height={25}
                        width={25}
                        style={{ cursor: "pointer" }}
                      />
                      {index === 0 &&
                        group.length > 1 &&
                        viewState.zoom > 15 && (
                          <div className={classes.markerCount}>
                            {group.length} {text.drivers}
                          </div>
                        )}
                    </Marker>

                    <Popup
                      key={`popup-${driver.item.dr_uid}`}
                      latitude={baseLat}
                      longitude={baseLng}
                      closeButton={false}
                      closeOnClick={false}
                      offset={25}
                    >
                      <p
                        onClick={() =>
                          !isDriverUrlRoute
                            ? handleDriverSelect(driver.item)
                            : undefined
                        }
                        className={classes.popupText}
                      >
                        {driver.driverName}
                      </p>
                    </Popup>
                  </div>
                ));
              }

              const spiderfy = getSpiderfyPositions(group);

              return spiderfy.map(({ lat, lng, driver }) => (
                <React.Fragment key={`spider-${driver.item.dr_uid}`}>
                  <Marker
                    key={`marker-${driver.item.dr_uid}`}
                    latitude={lat}
                    longitude={lng}
                    onClick={
                      !isDriverUrlRoute
                        ? () => handleDriverSelect(driver.item)
                        : undefined
                    }
                  >
                    <img
                      src={mapMarker}
                      alt=""
                      height={25}
                      width={25}
                      style={{ cursor: "pointer" }}
                    />
                  </Marker>

                  <Popup
                    key={`popup-${driver.item.dr_uid}`}
                    latitude={lat}
                    longitude={lng}
                    closeButton={false}
                    closeOnClick={false}
                    offset={25}
                  >
                    <p
                      className={classes.popupText}
                      onClick={() =>
                        !isDriverUrlRoute
                          ? handleDriverSelect(driver.item)
                          : undefined
                      }
                    >
                      {driver.driverName}
                    </p>
                  </Popup>
                </React.Fragment>
              ));
            })}
          </Map>
        ) : (
          <div className={classes.notificationsDiv}>{text.noCoordinates}</div>
        )}
      </div>
    </ModalWrapper>
  );
}
